import { createApi } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import uniqBy from 'lodash.uniqby';

import { axiosBaseQuery } from './queries';
import { parseFiltersData } from './contacts/filters';
import { mapCompanyDetailToV1, mapContactsV2ToV1 } from './contactsV2/formatter';

export const sharedListsApiV2 = createApi({
  reducerPath: 'sharedListsApiV2',
  baseQuery: (args) => axiosBaseQuery({ ...args, version: 'v2' }),
  tagTypes: ['shared-list', 'shared-list-view'],
  endpoints: (builder) => ({
    getContactsFromSharedList: builder.query({
      query: ({ slug, params, password }) => ({
        url: `/shared_lists/${slug}/contacts`,
        params: {
          ...parseFiltersData(params.filters, true),
          page: params.page,
          includeGlobalCustomFields: true,
          includeListsCustomFields: true,
        },
        ...(password
          ? { options: { headers: { 'X-Shared-List-Password': password, Accept: 'application/json' } } }
          : undefined),
      }),
      transformResponse: (response, _, arg) => ({
        meta: response.meta,
        page: arg.params.page,
        contacts: mapContactsV2ToV1(response),
      }),
      forceRefetch: ({ currentArg, previousArg }) => !isEqual(currentArg, previousArg),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { params } = queryArgs;
        const { filters: queryFilters = {} } = params || {};
        const effectiveFilters = { ...parseFiltersData(queryFilters, true) };
        const filters = queryString.stringify(effectiveFilters, {
          arrayFormat: 'bracket',
        });

        return `${endpointName}?${filters}`;
      },
      merge: (currentState, incomingState) => {
        Object.assign(currentState, {
          ...incomingState,
          contacts: uniqBy([...currentState.contacts, ...incomingState.contacts], 'id'),
        });
      },
    }),
    getSharedListContactById: builder.query({
      query: ({ slug, password, contactId }) => ({
        url: `/shared_lists/${slug}/contacts/${contactId}`,
        ...(password
          ? { options: { headers: { 'X-Shared-List-Password': password, Accept: 'application/json' } } }
          : undefined),
      }),
      providesTags: ['shared-list-contact'],
      transformResponse: mapContactsV2ToV1,
    }),
    getSharedListViews: builder.query({
      query: ({ collectionId, sharedListId }) => ({
        url: `/collections/${collectionId}/shared_lists/${sharedListId}/views`,
      }),
    }),
    getSharedListView: builder.query({
      query: ({ collectionId, sharedListId, id }) => ({
        url: `/collections/${collectionId}/shared_lists/${sharedListId}/views/${id}`,
      }),
    }),
    createSharedListView: builder.mutation({
      query: ({ collectionId, sharedListId, data }) => ({
        url: `/collections/${collectionId}/shared_lists/${sharedListId}/views`,
        method: 'POST',
        data,
      }),
      async onQueryStarted({ collectionId, sharedListId, data }, { dispatch, queryFulfilled }) {
        const tempId = `temp-${Date.now()}`;
        const patchResult = dispatch(
          sharedListsApiV2.util.updateQueryData('getSharedListViews', { collectionId, sharedListId }, (draft) => {
            draft.push({ ...data, id: tempId });
          }),
        );
        try {
          const { data: createdView } = await queryFulfilled;
          dispatch(
            sharedListsApiV2.util.updateQueryData('getSharedListViews', { collectionId, sharedListId }, (draft) => {
              const tempIndex = draft.findIndex((view) => view.id === tempId);
              if (tempIndex !== -1) {
                draft.splice(tempIndex, 1, createdView);
              }
            }),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateSharedListView: builder.mutation({
      query: ({ collectionId, sharedListId, id, data }) => ({
        url: `/collections/${collectionId}/shared_lists/${sharedListId}/views/${id}`,
        method: 'PATCH',
        data,
      }),
      async onQueryStarted({ collectionId, sharedListId, id, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          sharedListsApiV2.util.updateQueryData('getSharedListViews', { collectionId, sharedListId }, (draft) => {
            const viewIndex = draft.findIndex((v) => v.id === id);
            if (viewIndex !== -1) {
              draft.splice(viewIndex, 1, { ...draft[viewIndex], ...data });
            }
          }),
        );
        const singleViewPatch = dispatch(
          sharedListsApiV2.util.updateQueryData('getSharedListView', { collectionId, sharedListId, id }, (draft) => ({
            ...draft,
            ...data,
          })),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          singleViewPatch.undo();
        }
      },
    }),
    deleteSharedListView: builder.mutation({
      query: ({ collectionId, sharedListId, id }) => ({
        url: `/collections/${collectionId}/shared_lists/${sharedListId}/views/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ collectionId, sharedListId, id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          sharedListsApiV2.util.updateQueryData('getSharedListViews', { collectionId, sharedListId }, (draft) => {
            const index = draft.findIndex((view) => view.id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getSharedListCompanyDetailsFromContactById: builder.query({
      query: ({ slug, password, contactId }) => ({
        url: `/shared_lists/${slug}/contacts/${contactId}`,
        ...(password
          ? { options: { headers: { 'X-Shared-List-Password': password, Accept: 'application/json' } } }
          : undefined),
      }),
      providesTags: ['shared-list-contact'],
      transformResponse: mapCompanyDetailToV1,
    }),
  }),
});

export const {
  useGetContactsFromSharedListQuery,
  useGetSharedListCompanyDetailsFromContactByIdQuery,
  useLazyGetContactsFromSharedListQuery,
  useGetSharedListContactByIdQuery,
  useGetSharedListViewsQuery,
  useLazyGetSharedListViewsQuery,
  useGetSharedListViewQuery,
  useLazyGetSharedListViewQuery,
  useCreateSharedListViewMutation,
  useUpdateSharedListViewMutation,
  useDeleteSharedListViewMutation,
} = sharedListsApiV2;
